<template>
  <div data-testid="markdown-text">
    <template v-if="text && typeof text === 'string'">
      <VueMarkdown
        v-for="(lineBreak, line) in lines"
        :key="line"
        class="markdown-html"
        :class="className"
        :source="processTextWithKeywords(lineBreak, props.keywords)"
        :options="markdownOptions"
        :plugins="plugins"
      />
    </template>
    <template v-else-if="running"> <ArticleLoading v-for="i in 3" :key="i" /></template>
  </div>
</template>
<script lang="ts" setup>
import VueMarkdown from "vue-markdown-render";
import ArticleLoading from "@/components/loading/ArticleLoading.vue";
import { processTextWithKeywords } from "@/composables/processText";
import MarkdownIt from "markdown-it";
const props = withDefaults(
  defineProps<{
    running?: boolean;
    text: string | Record<string, any>;
    className?: string;
    keywords?: string[];
  }>(),
  {
    className: "markdown-default",
  }
);

const lines = computed(() => {
  const { text } = props;
  if (typeof text === "object") return {};
  return props.text.split("\\n");
});

const markdownOptions = {
  html: true,
  breaks: true,
  linkify: true,
  typographer: true,
};

function linkBlankPlugin(md: MarkdownIt) {
  // grab whatever renderer was there before
  const defaultRender =
    md.renderer.rules.link_open ||
    ((tokens, idx, opts, _env, self) => self.renderToken(tokens, idx, opts));

  md.renderer.rules.link_open = (tokens, idx, opts, env, self) => {
    const token = tokens[idx];
    // only if there's actually an href
    if (token.attrIndex("href") >= 0) {
      // add target if it's not already there
      if (token.attrIndex("target") < 0) {
        token.attrPush(["target", "_blank"]);
      }
      // always add a safe rel
      token.attrPush(["rel", "noopener noreferrer"]);
    }
    // fall back to default renderer
    return defaultRender(tokens, idx, opts, env, self);
  };
}

const plugins = [linkBlankPlugin];
</script>

<style>
@reference "@/assets/style.css";
.markdown-default h1 {
  @apply text-2xl font-bold mb-4 mt-8;
}
.markdown-default h2 {
  @apply text-lg font-bold mb-1 mt-6;
}
.markdown-default h3,
.markdown-default h4 {
  @apply text-base font-bold;
}

.markdown-default p {
  @apply mb-3;
}

.markdown-default li {
  @apply mb-1 ml-4 list-disc;
}
.markdown-default a {
  @apply text-primary-300 hover:text-primary-400 hover:underline;
}

.markdown-default a::after {
  font-family: "primeicons";
  speak: none;
  content: "\e93c";
  margin-left: 0.3rem;
  font-size: 0.7rem;
  vertical-align: text-bottom;
}
</style>
